import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gustoService, GustoAuthStatus } from '../services/gustoService';
import { getRuntimeEnv } from '../utils/runtimeEnv';
import { memberKeys } from './useUsers';

/**
 * Query Keys
 * Centralized query key factory for Gusto
 */
export const gustoKeys = {
  all: ['gusto'] as const,
  status: () => [...gustoKeys.all, 'status'] as const,
  unassignedUsers: () => [...gustoKeys.all, 'unassigned-users'] as const,
  syncStatus: () => [...gustoKeys.all, 'sync-status'] as const,
  myData: () => [...gustoKeys.all, 'my-data'] as const,
  memberData: (memberId: string) => [...gustoKeys.all, 'member-data', memberId] as const,
  contractorPayments: (contractorUuid: string, page: number, perPage: number) =>
    [...gustoKeys.all, 'contractor-payments', contractorUuid, page, perPage] as const,
};

/**
 * Hook: useGustoStatus
 * Fetch the Gusto OAuth connection status
 */
export const useGustoStatus = (enabled: boolean = true) => {
  return useQuery<GustoAuthStatus>({
    queryKey: gustoKeys.status(),
    queryFn: () => gustoService.getStatus(),
    enabled,
    staleTime: 0,
    refetchOnMount: true,
  });
};

/**
 * Reads the build/deploy-time Gusto kill switch via the shared runtime-env
 * helper (window.__env__ then process.env). The integration is enabled by
 * default and only hard-disabled when the flag is explicitly set to "false".
 */
export const isGustoEnvEnabled = (): boolean =>
  getRuntimeEnv('REACT_APP_GUSTO_ENABLED') !== 'false';

export interface GustoEnabledState {
  /** Build/deploy kill switch. When false the integration is fully hidden. */
  envEnabled: boolean;
  /** Kill switch AND backend reports the OAuth service is configured. */
  isConfigured: boolean;
  /** Kill switch AND the organization has an active Gusto connection. */
  isConnected: boolean;
  isLoading: boolean;
}

/**
 * Hook: useGustoEnabled
 * Single source of truth for whether Gusto UI should render. Combines the
 * build/deploy kill switch (REACT_APP_GUSTO_ENABLED) with the backend-driven
 * status (serviceConfigured / connected) so the integration self-disables
 * until Gusto credentials are configured and a connection is established.
 */
export const useGustoEnabled = (): GustoEnabledState => {
  const envEnabled = isGustoEnvEnabled();
  const { data: status, isLoading } = useGustoStatus(envEnabled);

  return {
    envEnabled,
    isConfigured: envEnabled && (status?.serviceConfigured ?? false),
    isConnected: envEnabled && (status?.connected ?? false),
    isLoading: envEnabled && isLoading,
  };
};

/**
 * Hook: useConnectGusto
 * Start the Gusto OAuth connect flow
 */
export const useConnectGusto = () => {
  return useMutation({
    mutationFn: async () => {
      const authorizationUrl = await gustoService.getAuthorizeUrl();
      window.location.href = authorizationUrl;
    },
  });
};

/**
 * Hook: useDisconnectGusto
 * Disconnect the Gusto integration
 */
export const useDisconnectGusto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => gustoService.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: gustoKeys.all });
    },
  });
};

/**
 * Hook: useUnassignedGustoUsers
 * Fetch Gusto users not linked to any member
 */
export const useUnassignedGustoUsers = (enabled: boolean = true) => {
  return useQuery({
    queryKey: gustoKeys.unassignedUsers(),
    queryFn: () => gustoService.listUnassignedGustoUsers(),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook: useAssignGustoUser
 * Assign a Gusto user to a member
 */
export const useAssignGustoUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ gustoUserId, memberId }: { gustoUserId: string; memberId: string }) =>
      gustoService.assignGustoUser(gustoUserId, memberId),
    onSuccess: (updatedMember) => {
      queryClient.invalidateQueries({ queryKey: gustoKeys.unassignedUsers() });
      queryClient.invalidateQueries({ queryKey: memberKeys.detail(updatedMember.id) });
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: gustoKeys.syncStatus() });
      queryClient.invalidateQueries({ queryKey: gustoKeys.memberData(updatedMember.id) });
    },
  });
};

/**
 * Hook: useUnassignGustoUser
 * Unassign a Gusto user from a member
 */
export const useUnassignGustoUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (gustoUserId: string) =>
      gustoService.unassignGustoUser(gustoUserId),
    onSuccess: (updatedMember) => {
      queryClient.invalidateQueries({ queryKey: gustoKeys.unassignedUsers() });
      queryClient.invalidateQueries({ queryKey: memberKeys.detail(updatedMember.id) });
      queryClient.invalidateQueries({ queryKey: memberKeys.lists() });
      queryClient.invalidateQueries({ queryKey: gustoKeys.syncStatus() });
      queryClient.invalidateQueries({ queryKey: gustoKeys.memberData(updatedMember.id) });
    },
  });
};

/**
 * Hook: useMyGustoData
 * Fetch the current user's own Gusto data
 */
export const useMyGustoData = () => {
  return useQuery({
    queryKey: gustoKeys.myData(),
    queryFn: () => gustoService.getMyGustoData(),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook: useMemberGustoData
 * Fetch the Gusto data for a specific member (manager/admin view).
 */
export const useMemberGustoData = (memberId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: gustoKeys.memberData(memberId),
    queryFn: () => gustoService.getMemberGustoData(memberId),
    enabled: enabled && !!memberId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

/** Default number of contractor payments fetched per page. */
export const CONTRACTOR_PAYMENTS_PER_PAGE = 25;

/**
 * Hook: useContractorPayments
 * Fetch a page of the Gusto contractor payment history for a given contractor
 * UUID (manager/admin view). `page` is 1-based. Only enabled when a contractor
 * UUID is present. Previous page data is kept while a new page loads so the
 * table doesn't flash empty during pagination.
 */
export const useContractorPayments = (
  contractorUuid: string,
  enabled: boolean = true,
  page: number = 1,
  perPage: number = CONTRACTOR_PAYMENTS_PER_PAGE
) => {
  return useQuery({
    queryKey: gustoKeys.contractorPayments(contractorUuid, page, perPage),
    queryFn: () => gustoService.getContractorPayments(contractorUuid, page, perPage),
    enabled: enabled && !!contractorUuid,
    staleTime: 5 * 60 * 1000,
    retry: false,
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Hook: useGustoSyncStatus
 * Get the latest sync status for the organization
 */
export const useGustoSyncStatus = () => {
  return useQuery({
    queryKey: gustoKeys.syncStatus(),
    queryFn: () => gustoService.getSyncStatus(),
    staleTime: 30 * 1000,
  });
};
